import { createContext } from 'react'
import type { StoredTokens } from '../api/client'
import type { CurrentUser, Role } from '../api/types'

export interface AuthState {
  user: CurrentUser | null
  /** True until the stored token has been exchanged for a `GET /auth/me`. */
  bootstrapping: boolean
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, companyName: string) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  /**
   * Stores a session that arrived outside `login`/`register` — currently the
   * QuickBooks sign-in callback, which redeems its code for tokens itself. Lives
   * here rather than in the page so the token store keeps exactly one writer.
   *
   * Resolves once the user is in state; await it before navigating into the
   * guarded app, or `RequireAuth` will see a null user and bounce.
   */
  acceptSession: (session: StoredTokens) => Promise<void>
}

export const AuthContext = createContext<AuthState | null>(null)

/** Capabilities the nav and route guard branch on, derived from the role. */
export type Capability = 'import' | 'export' | 'delete' | 'admin' | 'view'

const CAPABILITIES: Record<Role, Capability[]> = {
  Admin: ['import', 'export', 'delete', 'admin', 'view'],
  User: ['import', 'export', 'delete', 'view'],
  ImportOnly: ['import', 'view'],
  ExportOnly: ['export', 'view'],
  DeleteOnly: ['delete', 'view'],
  // A platform operator signing into a tenant workspace gets the full tenant surface.
  PlatformAdmin: ['import', 'export', 'delete', 'admin', 'view'],
}

export function capabilitiesFor(role: Role | undefined): Capability[] {
  return role ? (CAPABILITIES[role] ?? ['view']) : []
}

export function can(role: Role | undefined, capability: Capability): boolean {
  return capabilitiesFor(role).includes(capability)
}
